<script>
  import { onMount, onDestroy } from "svelte"
  import Chart from "chart.js/auto"
  import { navigate } from "svelte-routing"
  import { user } from "../../stores/User"
  import { BASE_URL } from "../../stores/BASE_URL"
  import { agruparEvolucionDeudas } from "../../services/evolucionDeudas"
  import Spinner from "../Spinner.svelte"
  import SinInformacion from "../Sin_informacion.svelte"

  export let documento
  export let tipo

  const soles = new Intl.NumberFormat("es-PE", {
    style: "currency",
    currency: "PEN",
  })
  let meses = []
  let loading = true
  let error = ""
  let montado = false
  let controller

  $: ultimoMes = meses[meses.length - 1]
  $: mesAnterior = meses[meses.length - 2]
  $: variacion =
    ultimoMes && mesAnterior
      ? Math.round((ultimoMes.monto - mesAnterior.monto) * 100) / 100
      : null
  $: porcentaje =
    variacion !== null && mesAnterior.monto !== 0
      ? new Intl.NumberFormat("es-PE", {
          style: "percent",
          maximumFractionDigits: 2,
        }).format(Math.abs(variacion / mesAnterior.monto))
      : null

  onMount(() => {
    montado = true
  })
  onDestroy(() => controller?.abort())
  $: if (montado) cargarDatos(documento, tipo)

  async function cargarDatos(documentoConsulta, tipoConsulta) {
    controller?.abort()
    const solicitud = new AbortController()
    controller = solicitud
    loading = true
    error = ""
    meses = []

    try {
      const params = new URLSearchParams({
        documento: documentoConsulta,
        tipo: tipoConsulta,
        ops: "evolucion_de_deuda",
        session_key: $user.session_key,
      })
      const response = await fetch(`${BASE_URL}persona.php?${params}`, {
        signal: solicitud.signal,
      })
      if (!response.ok)
        throw new Error("No se pudo cargar la evolución de deudas.")
      const resp = await response.json()
      if (solicitud.signal.aborted) return
      if (resp.status === "error") {
        navigate("/", { replace: true })
        return
      }
      if (!Array.isArray(resp.data))
        throw new Error("La respuesta de evolución de deudas no es válida.")
      meses = agruparEvolucionDeudas(resp.data, tipoConsulta)
    } catch (err) {
      if (!solicitud.signal.aborted) error = err.message
    } finally {
      if (!solicitud.signal.aborted) loading = false
    }
  }

  function crearGrafico(canvas, datos) {
    const chart = new Chart(canvas, {
      type: "line",
      data: {
        labels: datos.map((mes) => mes.mes),
        datasets: [
          {
            label: "Deuda total",
            data: datos.map((mes) => mes.monto),
            borderColor: "#f97316",
            backgroundColor: ({ chart }) => {
              const area = chart.chartArea
              if (!area) return "rgba(249, 115, 22, 0.12)"
              const degradado = chart.ctx.createLinearGradient(
                0,
                area.top,
                0,
                area.bottom,
              )
              degradado.addColorStop(0, "rgba(249, 115, 22, 0.20)")
              degradado.addColorStop(1, "rgba(249, 115, 22, 0.01)")
              return degradado
            },
            fill: "origin",
            pointBackgroundColor: "#f97316",
            borderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 7,
            pointHitRadius: 12,
            tension: 0,
            spanGaps: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "nearest", intersect: true },
        plugins: {
          legend: { display: false },
          tooltip: {
            displayColors: false,
            callbacks: {
              label: ({ dataIndex }) =>
                `Total: ${soles.format(datos[dataIndex].monto)}`,
              afterLabel: ({ dataIndex }) =>
                datos[dataIndex].entidades
                  .filter(({ monto }) => monto !== 0)
                  .map(
                    ({ entidad, monto }) =>
                      `${entidad}: ${soles.format(monto)}`,
                  ),
            },
          },
        },
        scales: {
          x: { ticks: { autoSkip: false, maxRotation: 45, minRotation: 45 } },
          y: {
            beginAtZero: true,
            ticks: { callback: (valor) => soles.format(valor) },
          },
        },
      },
    })
    return { destroy: () => chart.destroy() }
  }
</script>

<div class="d-flex flex-wrap align-items-start gap-5 mb-4">
  <slot name="tipo" />
  {#if !loading && !error && meses.length > 0}
    <div class="saldo-reciente">
      <p class="fg-secondary mb-1">Saldo más reciente · {ultimoMes.mes}</p>
      <div
        class="d-flex align-items-center py-1 fw-bold alert theme-primary mb-0"
      >
        {soles.format(ultimoMes.monto)}
      </div>
    </div>
    {#if variacion !== null}
      <div class="saldo-reciente variacion">
        <p class="fg-secondary mb-1">Variación respecto a {mesAnterior.mes}</p>
        <p
          class="d-flex align-items-center gap-1 py-1 fw-bold text-nowrap alert mb-0"
          class:theme-danger={variacion > 0}
          class:theme-success={variacion < 0}
        >
          {variacion > 0
            ? "Aumentó"
            : variacion < 0
              ? "Disminuyó"
              : "Sin cambios"}
          {soles.format(Math.abs(variacion))}
          {#if porcentaje !== null}
            ({porcentaje})
          {/if}
        </p>
      </div>
      {#if mesAnterior.monto === 0 && variacion !== 0}
        <div
          class="nota-variacion align-self-stretch d-flex align-items-end pt-4"
        >
          <p class="small fg-secondary mb-0">
            El saldo anterior era S/ 0; no aplica variación porcentual.
          </p>
        </div>
      {/if}
    {/if}
  {/if}
</div>

{#if loading}
  <Spinner />
{:else if error}
  <div class="alert theme-danger" role="alert">
    <p>{error}</p>
    <button
      class="btn btn-sm btn-outline theme-primary"
      type="button"
      on:click={() => cargarDatos(documento, tipo)}>Reintentar</button
    >
  </div>
{:else if meses.length === 0}
  <SinInformacion />
{:else}
  <div class="chart-scroll">
    <div class="chart-container" style="min-width: {Math.max(720, meses.length * 48)}px">
      <canvas
        use:crearGrafico={meses}
        aria-label="Evolución de deuda {tipo} de {documento}, últimos {meses.length} meses en soles"
      >
        Totales mensuales y entidades disponibles en la tabla siguiente.
      </canvas>
    </div>
  </div>
  <table class="visually-hidden">
    <caption>Evolución de deuda {tipo} de {documento}</caption>
    <thead
      ><tr
        ><th scope="col">Mes</th><th scope="col">Total en soles</th><th
          scope="col">Entidades</th
        ></tr
      ></thead
    >
    <tbody>
      {#each meses as mes}
        <tr>
          <td>{mes.mes}</td>
          <td
            >{mes.monto === null
              ? "Sin registros"
              : soles.format(mes.monto)}</td
          >
          <td
            >{mes.entidades
              .map(({ entidad, monto }) => `${entidad}: ${soles.format(monto)}`)
              .join("; ")}</td
          >
        </tr>
      {/each}
    </tbody>
  </table>
{/if}

<style>
  .saldo-reciente {
    flex: 0 1 220px;
    width: 100%;
  }
  .nota-variacion {
    flex: 1 1 220px;
  }
  .variacion {
    flex-basis: auto;
    width: max-content;
    max-width: 100%;
  }
  .chart-scroll {
    overflow-x: auto;
  }
  .chart-container {
    position: relative;
    height: 360px;
    min-width: 720px;
  }
</style>
